// 统一的 overview 数据准备层
// 职责：提供统一的分组、过滤、排序逻辑
// 消除 other.ts 和 overview/location-count/index.ts 的重复代码

import { sortArray } from "components/results/analysis-results/sortUtil";
import {
  AnalysisItem,
  LegendItem,
  LayerGroupType,
  RV,
  GroupTableType
} from "helpers/models";
import transformAnalysis from "report/common/analysis";
import combine from "report/common/combine";
import transformRule from "report/common/rule";

/**
 * 过滤规则：哪些图层在特定条件下需要被忽略
 */
const IGNORED_FILTER_RULES = {
  // 澳大利亚野火相关的特殊图层
  SPECIAL_LAYERS: ["DR_Subsidence_France_SwissRe", "WF_DistToBush_AUS_SwissRe"],
  // 这些图层如果只有以下值之一，则需要被忽略
  SPECIAL_VALUES: ["Outside", "No Data", "Negligible", "No Observation"]
};

/**
 * 计算需要被忽略的图层列表
 *
 * 规则：
 * 1. 如果是特殊图层（澳洲特定图层），且其唯一值在特定列表中，则忽略
 * 2. 如果所有值都是 "No Data"，则忽略
 */
function getIgnoredLayerIds(
  grouped: Record<string, Record<string, any>>
): string[] {
  return Object.keys(grouped).filter((key) => {
    const values = Object.keys(grouped[key]);

    // 特殊图层规则
    if (IGNORED_FILTER_RULES.SPECIAL_LAYERS.includes(key)) {
      return (
        values.length === 1 &&
        IGNORED_FILTER_RULES.SPECIAL_VALUES.includes(values[0])
      );
    }

    // 通用规则：所有值都是 "No Data" 则忽略
    return values.every((value) => value === "No Data");
  });
}

/**
 * Overview 相关的数据准备结果
 */
export interface PreparedOverviewData {
  // 排序后的分组数据，格式为 { [layerId]: { [valueLabel]: details } }
  sorted: GroupTableType;

  // 被过滤掉的图层ID列表，用于显示"为什么这些图层没有出现在统计中"
  ignoredLayerIds: string[];

  // 转换后的规则，供后续渲染使用
  legend: LayerGroupType;
}

/**
 * 准备 overview 相关的分组数据
 *
 * 这个函数统一了 hazard/other.ts 和 overview/location-count 的数据准备逻辑
 * 避免重复代码，确保统计口径一致
 *
 * 流程：
 * 1. 初步分组：按 layerId 和 valueLabel 分组
 * 2. 计算忽略列表：根据规则识别需要被忽略的图层
 * 3. 过滤数据：删除忽略的图层
 * 4. 转换规则：将 legendItems 转换为内部格式
 * 5. 合并数据：组合分组数据和规则
 * 6. 排序：按 sortArray 顺序排序
 *
 * @param analysisItems 分析项数据
 * @param legendItems 图例项数据
 * @returns 准备好的数据结构
 */
export const buildPreparedHazardOverviewData = (
  analysisItems: AnalysisItem[],
  legendItems: LegendItem[]
): PreparedOverviewData => {
  // 第一步：初步分组
  const grouped = transformAnalysis(analysisItems);

  // 第二步：计算需要忽略的图层
  const ignoredLayerIds = getIgnoredLayerIds(grouped);

  // 第三步：过滤数据（删除忽略的图层）
  const filteredData = Object.keys(grouped).reduce((result, key) => {
    if (!ignoredLayerIds.includes(key)) {
      result[key] = grouped[key];
    }
    return result;
  }, {} as RV);

  // 第四步：转换规则和合并
  const legend: LayerGroupType = transformRule(legendItems);
  const together: GroupTableType = combine(filteredData, legend);

  // 第五步：按 sortArray 排序
  const sorted = Object.entries(together)
    .sort(([a], [b]) => {
      return sortArray.indexOf(a) - sortArray.indexOf(b);
    })
    .reduce((acc: GroupTableType, [key, val]) => {
      acc[key] = val;
      return acc;
    }, {});

  return {
    sorted,
    ignoredLayerIds,
    legend
  };
};
